import { useEffect, useState } from 'react'

// ---------------------------------------------------------------------------
// useMediaQuery — subscribe to a CSS media query and re-render on change.
//
// Surface-agnostic, no privileged data. Lets a page branch its render on
// viewport (e.g. drop low-value table columns / render stacked cards on phones)
// without duplicating the CSS breakpoint in JS ad-hoc.
//
// SSR / test environments without matchMedia default to `false` (desktop-first),
// matching the app's desktop baseline.
// ---------------------------------------------------------------------------

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/** Convenience: true at the app's phone breakpoint (≤640px). */
export function useIsPhone(): boolean {
  return useMediaQuery('(max-width: 640px)')
}
